import { useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import {
  technologyButtons,
  lineTypeButtons,
  productButtons,
  Button,
  TechnologyValues,
  LineTypeValues,
  SegmentValues,
} from './SaleConfiguratorButtons'

import { changeSaleFlow } from '../../store/sale.reducer'
import { selectSaleFlowType, selectButtonsConfig } from '../../store/sale.selectors'

import { fetchAgentData } from '~/agent/store/agent.reducer'
import { selectSaleId } from '~/agent/store/agent.selectors'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import { RootState } from '~/core/store/root.reducer'
import { NO_TAX, TAX_IVA } from '~/prices/contstants/taxes'
import { setTaxData } from '~/prices/store/prices.reducer'
import PATHS from '~/router/paths'

interface UseSaleConfigurator {
  techButtonsFormatted: Button[]
  lineTypeButtonsFormatted: Button[]
  productButtonsFormatted: Button[]
  shouldDisable: boolean
  shouldShowLineType: boolean
  onStartSale: () => { [key: string]: unknown } | void
  updateStateKey: (key: string, value: string) => void
  onSetProduct: (key: string, value: string) => void
  onSetTechnology: (key: string, value: string) => void
  technology: string
  lineType: string
  product: string
}

interface Segments {
  residential?: boolean
  pro?: boolean
}

interface LineTypes extends Segments {
  prepaid?: Segments
  postpaid?: Segments
  fixed?: Segments
}

interface SaleConfig {
  energy?: LineTypes
  convergent?: LineTypes
  '2p'?: LineTypes
  mobileOnly?: LineTypes
}

export default function useSaleConfigurator(): UseSaleConfigurator {
  const history = useHistory()
  const dispatch = useDispatch()
  const sale = useSelector(selectSaleFlowType)
  const saleId = useSelector(selectSaleId)
  const { technology, lineType, product } = sale
  const { energyUrl, environment } = useAppDepsContext().runtimeConfig
  const config: SaleConfig = useSelector((state: RootState) =>
    selectButtonsConfig(state, environment),
  )

  function isTechnology(
    arg: TechnologyValues | LineTypeValues | SegmentValues,
  ): arg is TechnologyValues {
    return ['mobileOnly', 'convergent', '2p', 'energy', 'leads'].some((element) => element === arg)
  }

  function isSegment(arg: TechnologyValues | LineTypeValues | SegmentValues): arg is SegmentValues {
    return ['residential', 'pro'].some((element) => element === arg)
  }

  function setButtonsStatus(
    buttons: Button[],
    technologyVal?: TechnologyValues,
    lineTypeVal?: LineTypeValues,
  ) {
    const technologies = (technologyVal ? config[technologyVal] : {}) || {}
    return buttons.map((button) => {
      let disabled = true
      if (isTechnology(button.value)) {
        disabled = !config[button.value]
      } else if (lineTypeVal && isSegment(button.value)) {
        const lineTypes = technologies[lineTypeVal] || {}
        disabled = !lineTypes[button.value]
      } else {
        disabled = !technologies[button.value]
      }
      return {
        ...button,
        disabled,
      }
    })
  }

  useEffect(() => {
    if (!saleId) {
      dispatch(fetchAgentData())
    }
  }, [saleId])

  const isMobileOnlySelected = useMemo(() => technology === 'mobileOnly', [technology])

  const isEnergySelected = useMemo(() => technology === 'energy', [technology])

  const isLeadsSelected = useMemo(() => technology === 'leads', [technology])

  const shouldDisable = useMemo(() => {
    const filteredValues = Object.values(sale).filter((elem) => elem)
    const requiredValues = isMobileOnlySelected ? 3 : 2
    const requiredValuesWithEnergy = isEnergySelected || isLeadsSelected ? 1 : requiredValues
    return filteredValues.length !== requiredValuesWithEnergy
  }, [sale, isMobileOnlySelected, isEnergySelected])

  const techButtonsFormatted = useMemo(() => setButtonsStatus(technologyButtons), [])

  const lineTypeButtonsFormatted = useMemo(() => setButtonsStatus(lineTypeButtons, technology), [
    technology,
  ])

  const productButtonsFormatted = useMemo(
    () => setButtonsStatus(productButtons, technology, lineType),
    [lineType, technology],
  )

  function onStartSale() {
    if (isEnergySelected) {
      window.location.href = `${energyUrl}/signups/new?auth_schema=auth-masmovil`
    } else if (isLeadsSelected) {
      history.push({ pathname: PATHS.LEADS })
    } else {
      history.push({ pathname: PATHS.OFFER_SETUP })
    }
  }

  function updateStateKey(key: string, value: string) {
    dispatch(changeSaleFlow({ ...sale, [key]: value }))
  }

  function onSetTechnology(key: string, value: string) {
    dispatch(changeSaleFlow({ product: '', lineType: '', [key]: value }))
  }

  function onSetProduct(key: string, value: string) {
    updateStateKey(key, value)
    dispatch(setTaxData(value === 'pro' ? NO_TAX : TAX_IVA))
  }

  useEffect(() => {
    const button = productButtonsFormatted.find((elem) => elem.value === sale.product)
    if (button?.disabled) {
      onSetProduct('product', '')
    }
  }, [productButtonsFormatted])

  return {
    techButtonsFormatted,
    lineTypeButtonsFormatted,
    productButtonsFormatted,
    shouldDisable,
    shouldShowLineType: isMobileOnlySelected,
    onStartSale,
    updateStateKey,
    onSetProduct,
    onSetTechnology,
    technology,
    lineType,
    product,
  }
}
